import authService from '../services/authService';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';

let failedQueue: Array<{
  resolve: (token: string) => void;
  reject: (reason?: any) => void;
}> = [];
let isRefreshing = false;

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach((request) => {
    if (error) {
      request.reject(error);
      return;
    }

    if (token) {
      request.resolve(token);
    } else {
      request.reject(new Error('Token refresh failed'));
    }
  });

  failedQueue = [];
};

const isFormData = (value: any): value is FormData => {
  return typeof FormData !== 'undefined' && value instanceof FormData;
};

const isRefreshEndpoint = (endpoint: string): boolean => {
  return endpoint.includes('/api/v1/auth/refresh');
};

const buildHeaders = (accessToken?: string, payload?: any): Record<string, string> => {
  const headers: Record<string, string> = {};

  if (!isFormData(payload)) {
    headers['Content-Type'] = 'application/json';
  }

  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
  }

  return headers;
};

const refreshAccessToken = async (): Promise<string> => {
  const refreshToken = authService.getRefreshToken();
  if (!refreshToken) {
    throw new Error('No refresh token available');
  }

  if (isRefreshing) {
    return new Promise<string>((resolve, reject) => {
      failedQueue.push({ resolve, reject });
    });
  }

  isRefreshing = true;
  try {
    const refreshResponse = await authService.refreshToken();
    const newToken = refreshResponse?.data?.accessToken;

    if (!refreshResponse?.success || !newToken) {
      throw new Error('Token refresh failed');
    }

    processQueue(null, newToken);
    return newToken;
  } catch (error) {
    processQueue(error, null);
    throw error;
  } finally {
    isRefreshing = false;
  }
};

async function getWithAuthRetry(url: string, signal?: AbortSignal): Promise<Response> {
  const fetchOnce = (token: string | undefined) =>
    fetch(url, {
      method: 'GET',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      signal,
    });

  let accessToken = authService.getAccessToken() || undefined;
  let response = await fetchOnce(accessToken);

  if (response.status === 401 && accessToken) {
    try {
      await refreshAccessToken();
      accessToken = authService.getAccessToken() || undefined;
      response = await fetchOnce(accessToken);
    } catch (error) {
      authService.logout();
      window.location.href = '/?auth=login';
      throw error;
    }
  }

  return response;
}

/** Backend often returns `{ error: { message, code } }` (not a string on `error`). */
function parseJsonErrorMessage(data: unknown): string | undefined {
  if (!data || typeof data !== 'object') return undefined;
  const d = data as Record<string, unknown>;
  const nested = d.error;
  if (nested && typeof nested === 'object' && nested !== null) {
    const m = (nested as { message?: unknown }).message;
    if (typeof m === 'string' && m.trim()) return m.trim();
  }
  if (typeof nested === 'string' && nested.trim()) return nested.trim();
  if (typeof d.message === 'string' && d.message.trim()) return d.message.trim();
  return undefined;
}

async function throwIfGetNotOk(response: Response): Promise<void> {
  if (response.ok) return;

  let message = `HTTP ${response.status}`;
  try {
    const ct = response.headers.get('content-type');
    if (ct && ct.includes('application/json')) {
      const data = await response.json();
      message = parseJsonErrorMessage(data) || message;
    } else {
      const text = await response.text();
      if (text) message = text.slice(0, 200);
    }
  } catch {
    /* keep message */
  }
  throw new Error(message);
}

/**
 * Returns `/api/v1/exports/...` path when href targets the authenticated PDF export API
 * (relative, or same-origin / frontend-dev absolute URL).
 */
export function parseExportDownloadPath(href: string | undefined): string | null {
  if (!href || href.startsWith('#')) return null;
  try {
    if (href.startsWith('/api/v1/exports/')) {
      return href.split('?')[0];
    }
    const base =
      typeof window !== 'undefined' && window.location?.origin
        ? window.location.origin
        : 'http://localhost';
    const u = new URL(href, base);
    if (u.pathname.startsWith('/api/v1/exports/')) {
      return u.pathname.split('?')[0];
    }
  } catch {
    return null;
  }
  return null;
}

/**
 * GET an export (PDF/PNG) from `/api/v1/exports/...` with the user JWT (refresh on 401)
 * and return an object URL for inline display (e.g. chart images in chat).
 * Plain `<img src>` fails because browsers do not send Authorization headers.
 * Caller must `URL.revokeObjectURL()` the returned URL when done.
 */
export async function fetchAuthenticatedExportObjectUrl(exportPath: string): Promise<string> {
  if (!exportPath.startsWith('/api/v1/exports/')) {
    throw new Error('Invalid export path');
  }
  const response = await getWithAuthRetry(`${API_BASE_URL}${exportPath}`);
  await throwIfGetNotOk(response);
  const blob = await response.blob();
  return URL.createObjectURL(blob);
}

/**
 * GET a PDF from `/api/v1/exports/...` with the user JWT (and refresh on 401), then trigger a file download.
 * Plain navigation to this URL fails because browsers do not send Authorization headers.
 */
export async function downloadAuthenticatedExport(exportPath: string): Promise<void> {
  if (!exportPath.startsWith('/api/v1/exports/')) {
    throw new Error('Invalid export path');
  }

  const response = await getWithAuthRetry(`${API_BASE_URL}${exportPath}`);
  await throwIfGetNotOk(response);

  const blob = await response.blob();
  const segment = exportPath.split('/').pop() || 'export.pdf';
  // Preserve real extensions (chart PNGs share this route); default to .pdf.
  const hasKnownExt = /\.(pdf|png)$/i.test(segment);
  const filename = hasKnownExt ? segment : `${segment}.pdf`;
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = objectUrl;
  a.download = filename;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(objectUrl);
}

/**
 * UI lists prefix file rows with `file-<uuid>` (see useDataSources); the API expects the raw UUID.
 */
export function normalizeDataSourceFileIdForApi(id: string): string {
  return id.startsWith('file-') ? id.slice(5) : id;
}

/**
 * GET `/api/v1/data-sources/files/:id/view` with JWT, refresh on 401, then return the body as a Blob.
 * Used by the in-app PDF viewer (same auth behavior as `downloadAuthenticatedDataSourceFile`).
 */
export async function fetchDataSourceFileViewBlob(
  sourceId: string,
  signal?: AbortSignal,
): Promise<Blob> {
  const apiId = normalizeDataSourceFileIdForApi(sourceId);
  const path = `/api/v1/data-sources/files/${encodeURIComponent(apiId)}/view`;
  const response = await getWithAuthRetry(`${API_BASE_URL}${path}`, signal);
  await throwIfGetNotOk(response);
  return response.blob();
}

/**
 * GET uploaded file bytes from `/api/v1/data-sources/files/:id/view` with JWT (same as in-app viewer), then save.
 */
export async function downloadAuthenticatedDataSourceFile(
  sourceId: string,
  downloadFilename: string,
): Promise<void> {
  const blob = await fetchDataSourceFileViewBlob(sourceId);
  const safe = downloadFilename.trim() || 'document';
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = objectUrl;
  a.download = safe;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.setTimeout(() => URL.revokeObjectURL(objectUrl), 500);
}

const executeRequest = async <T = any>(
  endpoint: string,
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE',
  payload?: any,
  token?: string,
  hasRetried = false
): Promise<T> => {
  const accessToken = token || authService.getAccessToken() || undefined;
  const headers = buildHeaders(accessToken, payload);
  const requestBody = payload === undefined ? undefined : (isFormData(payload) ? payload : JSON.stringify(payload));

  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    method,
    headers,
    body: requestBody,
  });

  return handleResponse<T>(response, {
    endpoint,
    canRefresh: Boolean(accessToken) && !hasRetried,
    retryFunction: () => executeRequest<T>(endpoint, method, payload, undefined, true),
  });
};

export const apiClient = {
  get: async <T = any>(endpoint: string, token?: string): Promise<T> => {
    return executeRequest<T>(endpoint, 'GET', undefined, token);
  },

  post: async <T = any>(endpoint: string, data: any, token?: string): Promise<T> => {
    return executeRequest<T>(endpoint, 'POST', data, token);
  },

  put: async <T = any>(endpoint: string, data: any, token?: string): Promise<T> => {
    return executeRequest<T>(endpoint, 'PUT', data, token);
  },

  patch: async <T = any>(endpoint: string, data: any, token?: string): Promise<T> => {
    return executeRequest<T>(endpoint, 'PATCH', data, token);
  },

  delete: async <T = any>(endpoint: string, token?: string): Promise<T> => {
    return executeRequest<T>(endpoint, 'DELETE', undefined, token);
  },
};

const handleResponse = async <T = any>(
  response: Response,
  options: {
    endpoint: string;
    canRefresh: boolean;
    retryFunction?: () => Promise<T>;
  }
): Promise<T> => {
  const contentType = response.headers.get('content-type');
  let data: any;

  try {
    if (contentType && contentType.includes('application/json')) {
      data = await response.json();
    } else {
      data = { message: await response.text() };
    }
  } catch (_parseError) {
    data = { message: response.statusText || 'Request failed' };
  }

  if (response.status === 401 && options.canRefresh && !isRefreshEndpoint(options.endpoint)) {
    try {
      await refreshAccessToken();

      if (options.retryFunction) {
        return options.retryFunction();
      }

      throw new Error('No retry function available');
    } catch (error) {
      authService.logout();
      window.location.href = '/?auth=login';
      throw error;
    }
  }

  if (!response.ok) {
    const backendMessage =
      parseJsonErrorMessage(data) || (typeof data === 'string' ? data : undefined);
    throw new Error(backendMessage || `HTTP error! status: ${response.status}`);
  }

  return data;
};
